import { FolderKanban, Plus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { SortHeader, useEntitySort } from "@/components/EntitySort";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Entity } from "@/lib/api";
import { formatDate } from "@/lib/format";

/**
 * UI-19's list — the initiative manager. Initiatives are entities of type `initiative`
 * (M16.1 amended the entity manager for them), but they get their own room here rather than
 * being a filter on Entities: an initiative is where the user works day to day, not a record
 * occasionally corrected, and a first-class list makes that true of navigation too.
 *
 * Filtering happens in the browser over the same `entities` list Entities reads, exactly the
 * way that page already narrows by type — this is that narrowing, permanently applied, with
 * columns built for what an initiative actually carries (goals) rather than what a generic
 * entity does (aliases, description).
 */
export function InitiativesPage() {
  const { entities, entitiesLoading, entitiesProblem } = useApp();
  const [adding, setAdding] = useState(false);

  const initiatives = useMemo(() => entities.filter((entity) => entity.type === "initiative"), [entities]);
  const { sorted: shown, sort, toggleSort } = useEntitySort(initiatives);

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-start justify-between gap-[var(--space-4)]">
        <div className="flex flex-wrap items-baseline gap-[var(--space-4)]">
          <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            Initiatives
          </h1>
          <p className="max-w-[64ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
            The projects and pushes you're running, each with a main goal and its own dossier.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
        >
          <Plus size={15} strokeWidth={2.5} />
          Add initiative
        </button>
      </header>

      {entitiesProblem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {entitiesProblem}
        </p>
      )}

      {entitiesLoading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : shown.length === 0 ? (
        <Empty onAdd={() => setAdding(true)} />
      ) : (
        <div className="flex flex-col overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border-subtle)]">
          <div className="grid grid-cols-[1.2fr_2fr_0.8fr_0.7fr_0.9fr] gap-3 bg-[var(--surface-2)] px-[var(--space-4)] py-2.5 text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wide)] text-[var(--text-muted)]">
            <SortHeader label="Name" active={sort} me="name" onClick={toggleSort} />
            <span>Main goal</span>
            <span>Secondary goals</span>
            <SortHeader label="Mentions" active={sort} me="mentions" onClick={toggleSort} />
            <SortHeader label="Last heard" active={sort} me="last" onClick={toggleSort} />
          </div>

          {shown.map((entity) => (
            <Row key={entity.id} entity={entity} />
          ))}
        </div>
      )}

      {adding && <AddInitiativeDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

function Row({ entity }: { entity: Entity }) {
  return (
    <Link
      to={`/initiatives/${entity.id}`}
      className="grid grid-cols-[1.2fr_2fr_0.8fr_0.7fr_0.9fr] items-center gap-3 border-t border-[var(--border-subtle)] px-[var(--space-4)] py-3 text-[length:var(--text-sm)] transition-[box-shadow,border-color] hover:border-[var(--border-default)] hover:shadow-[var(--shadow-md)]"
    >
      <span className="flex w-fit items-center gap-2 font-semibold text-[var(--vl-virtual-soft)]">
        <FolderKanban size={14} strokeWidth={2} className="shrink-0" aria-hidden />
        <span className="truncate">{entity.name}</span>
      </span>
      <span className="truncate text-[var(--text-secondary)]">{entity.mainGoal ?? "—"}</span>
      <span className="tabular-nums text-[var(--text-muted)]">{entity.secondaryGoals.length}</span>
      <span className="tabular-nums text-[var(--text-muted)]">{entity.mentionCount}</span>
      <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {entity.lastMentionedAt ? formatDate(entity.lastMentionedAt) : "—"}
      </span>
    </Link>
  );
}

function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
      <div className="mb-[var(--space-4)] flex h-12 w-12 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
        <FolderKanban size={22} strokeWidth={2} className="text-[var(--text-muted)]" />
      </div>

      <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">No initiatives yet</p>
      <p className="mt-1 max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Add the projects and pushes you're running — each gets a main goal, secondary goals and its
        own dossier of beliefs.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-[var(--space-4)] inline-flex h-9 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
      >
        <Plus size={14} strokeWidth={2.5} />
        Add initiative
      </button>
    </div>
  );
}

/**
 * A portal to the document body rather than an in-flow modal, for the same reason
 * Entities' AddEntityDialog is one: the content frame is zoomed and clips its overflow.
 */
function AddInitiativeDialog({ onClose }: { onClose: () => void }) {
  const { api, refreshEntities } = useApp();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit() {
    const trimmed = name.trim();
    if (trimmed.length === 0 || busy) return;

    setBusy(true);
    setProblem(null);
    try {
      const created = await api.createEntity({ type: "initiative", name: trimmed, description: "" });
      refreshEntities();
      // Straight into the new dossier, so adding one and setting its goal is one motion.
      navigate(`/initiatives/${created.id}`);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The initiative could not be added.");
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[color-mix(in_oklch,var(--background)_60%,transparent)] p-4 backdrop-blur-[var(--blur-sm)]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add initiative"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[480px] rounded-[var(--radius-xl)] border border-[var(--border-default)] bg-[var(--surface-1)] p-[var(--space-6)] shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <div>
            <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
              Add initiative
            </h2>
            <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Give it a name — the main goal, secondary goals and beliefs are set on its own page.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        <div className="mt-[var(--space-6)] flex flex-col gap-[var(--space-4)]">
          <label className="flex flex-col gap-1.5">
            <span className="text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">Name</span>
            <input
              ref={nameRef}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void submit()}
              placeholder="e.g. Q3 supplier onboarding"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </label>

          {problem && (
            <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
              {problem}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || name.trim().length === 0}
              className="h-8 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Adding…" : "Add initiative"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
